import React, { useMemo, CSSProperties } from "react";
import { useDataViewStyles } from "./css";
import { assureArray } from "../utils/validate";

export type DataViewItemType = {
  type: string;
  value?: any;
  key?: string;
  color?: string;
  title?: string;
  data?: {
    schema: DataViewItemType | DataViewItemType[];
  };
};

export interface DataViewProps {
  schema: DataViewItemType[];
}

const DataView: React.FC<DataViewProps> = (props) => {
  if (!Array.isArray(props.schema)) return null;
  const { classes } = useDataViewStyles();

  return (
   <div className={classes.container}>
      <div className={classes.dataViewTable}>
      <table>
        {props.schema.map((item, key) => {
          return <DataViewItem key={key} item={item} />;
        })}
      </table>
      </div>
    </div>
  );
};

interface DataViewItemProps {
  item: DataViewItemType;
}

const DataViewItem: React.FC<DataViewItemProps> = (props) => {
  if (props.item.type === "inline") {
    return <DataViewInline item={props.item} />;
  } else if (props.item.type === "block") {
    return <DataViewBlock item={props.item} />;
  } else if (props.item.type === "list") {
    return <DataViewList item={props.item} />;
  } else if (props.item.type === "link") {
    return <DataViewLink item={props.item} />;
  } else if (props.item.type === "space") {
    return <DataViewSpace />;
   } else if (props.item.type === "IMG_LINK") {  
    return <DataViewImage item={props.item} />;
  } else {
    return null;
  }
};

interface DataViewInlineProps {
  item: DataViewItemType;
}

const DataViewInline: React.FC<DataViewInlineProps> = (props) => {
  const { cx } = useDataViewStyles();
  const valueStyle = useMemo(() => {
    const style: CSSProperties = {};
    if (props.item.color) {
      style["color"] = props.item.color;
    }
    return style;
  }, [props.item.color]);

  return (
    <tbody>
      <tr className="data-view-inline">
        <td
          className={cx("data-view-key", {
            "data-view-key--empty": !props.item.key,
          })}
        >
          {props.item.key}
        </td>
        <td
          className={cx("data-view-value", {
            "data-view-value--empty": !props.item.value,
          })}
          style={valueStyle}
        >
          {props.item.value}
        </td>
      </tr>
    </tbody>
  );
};

interface DataViewBlockProps {
  item: DataViewItemType;
}

const DataViewBlock: React.FC<DataViewBlockProps> = (props) => {
  const { cx } = useDataViewStyles();
  return (
    <tbody>
      <tr className="data-view-block-top">
        <td className={cx("data-view-key")} colSpan={2}>
          {props.item.key}
        </td>
      </tr>
      <tr className="data-view-block-bottom">
        <td colSpan={2} className="data-view-value">
          {props.item.value &&
            props.item.value.split("\n").map((line: string, index: any) => {
              return <p key={index}>{line}</p>;
            })}
        </td>
      </tr>
    </tbody>
  );
};

const DataViewSpace: React.FC = () => {
  return (
    <tbody>
      <tr className="data-view-space" style={{ height: 10 }}></tr>
    </tbody>
  );
};






const DataViewListItemSpace: React.FC = () => {
  return <tr className="data-view-list-space" style={{ height: 10 }}></tr>;
};

interface DataViewLinkProps {
  item: DataViewItemType;
}

const DataViewLink: React.FC<DataViewLinkProps> = (props) => {
  const { cx } = useDataViewStyles();
  return (
    <tbody>
      <tr className="data-view-link">
        <td
          className={cx("data-view-key", {
            "data-view-key--empty": !props.item.key,
          })}
        >
          {props.item.key}
        </td>
        <td
          className={cx("data-view-value", {
            "data-view-value--empty": !props.item.value,
            "data-view-value--only-link": !props.item.title,
          })}
        >
          <a href={props.item.value} target="__blank">
            {props.item.title ?? props.item.value}
          </a>
        </td>
      </tr>
    </tbody>
  );
};

interface DataViewListItemInlineProps {
  item: DataViewItemType;
}

const DataViewListItemInline: React.FC<DataViewListItemInlineProps> = (
  props
) => {
  const { cx } = useDataViewStyles();
  const valueStyle = useMemo(() => {
    const style: CSSProperties = {};
    if (props.item.color) {
      style["color"] = props.item.color;
    }
    return style;
  }, [props.item.color]);

  return (
    <tr className="data-view-list-inline">
      <td
        className={cx("data-view-key", {
          "data-view-key--empty": !props.item.key,
        })}
      >
        {props.item.key}
      </td>
      <td
        className={cx("data-view-value", {
          "data-view-value--empty": !props.item.value,
        })}
        style={valueStyle}
      >
        {props.item.value}
      </td>
    </tr>
  );
};

interface DataViewListProps {
  item: {
    type: string;
    key?: string;
    data?: {
      schema: DataViewItemType | DataViewItemType[];
    };
  };
}

const DataViewList: React.FC<DataViewListProps> = (props) => {
  const schema = props.item.data?.schema
    ? assureArray(props.item.data?.schema)
    : [];

  return (
    <>
      <tbody>
        <tr className="data-view-list-top">
          <td className="data-view-key" colSpan={2}>
            {props.item.key}
          </td>
        </tr>
      </tbody>
      <tbody className="data-view-list">
        {schema.map((item, index) => {
  if (item.type === "inline") {
    return <DataViewListItemInline key={index} item={item} />;
  } else if (item.type === "link") {
    return (
      <tr key={index} className="data-view-list-link">
        <td className="data-view-key">{item.key}</td>
        <td className="data-view-value">
          <a href={item.value} target="__blank">
            {item.title ?? item.value}
          </a>
        </td>
      </tr>
    );
  } else if (item.type === "IMG_LINK") {
    return <DataViewListItemImage key={index} item={item} />;
  } else if (item.type === "space") {
    return <DataViewListItemSpace key={index} />;
  } else {
    return null;
  }
})}

      </tbody>
      <tbody>
        <DataViewListItemSpace />
      </tbody>
    </>
  );
};

interface DataViewImageProps {
  item: DataViewItemType;
}

const DataViewImage: React.FC<DataViewImageProps> = (props) => {
  return (
    <tbody>
      <tr className="data-view-image">
        <td colSpan={2} className="data-view-value" style={{ textAlign: "center" }}>
          <img
            src={props.item.value}
            alt={props.item.key}
            style={{
              maxWidth: "100%",
              maxHeight: "200px",
              objectFit: "contain",
              display: "block",  
              marginLeft: "auto", 
              marginRight: "auto", 
            }}
          />
        </td>
      </tr>
    </tbody>
  );
};

const DataViewListItemImage: React.FC<DataViewImageProps> = (props) => {
  return (
    <tr className="data-view-list-image">
      <td colSpan={2} className="data-view-value" style={{ textAlign: "center" }}>
        <img
          src={props.item.value}
          alt={props.item.key}
          style={{
            maxWidth: "100%",
            maxHeight: "200px",
            objectFit: "contain",
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        />
      </td>
    </tr>
  );
};





export default DataView;
